import { useRef } from "react";
import type { CSSProperties } from "react";
import { useFlip } from "./flip";
import { CHIP_MS } from "@libs/checkout-core";
import { MOTION, MOVE_EASE } from "./motion";
import { button, size, text } from "./ui";

/** The address the page verified, as the one row it is here. */
export function TicketsDestination({
  email,
  verified,
  checking,
  disabled,
  settled = false,
  guest = false,
  label = "Tickets destination",
  onChange,
}: {
  email: string;
  verified: boolean;
  checking: boolean;
  disabled: boolean;
  /** Paying: the label goes and the chip, unchanged, moves to the middle
   *  to stand over the payment's status. */
  settled?: boolean;
  /** A guest's address: no verification mark at all (not an empty one). */
  guest?: boolean;
  /** What the address is for: the tickets' unless the cart sells
   *  something else (`CHECKOUT_WORDS`). */
  label?: string;
  /** Absent: the address cannot be changed here (the Lissen app's own
   *  sign-in), so no × is drawn at all. */
  onChange?: () => void;
}) {
  const chip = useRef<HTMLSpanElement>(null);
  useFlip(chip, settled, { easing: MOVE_EASE });
  const fade = (shown: boolean): CSSProperties => ({
    opacity: shown ? 1 : 0,
    transition: `opacity ${shown ? 350 : 200}ms ease`,
  });
  return (
    // A grid whose chip column may shrink to nothing: a long address takes
    // an ellipsis instead of widening the checkout past its frame (which
    // clips it, × and all).
    <div
      style={{
        alignItems: "center",
        columnGap: "calc(var(--lc-space) * 1.5)",
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        minWidth: 0,
        position: "relative",
      }}
    >
      <span
        aria-hidden={settled || undefined}
        style={{
          ...text.strong,
          ...fade(!settled),
          fontSize: size.small,
          whiteSpace: "nowrap",
          // Out of the row once settled, so the chip may take its middle.
          ...(settled ? { left: 0, position: "absolute" } : {}),
        }}
      >
        {label}
      </span>
      <span
        ref={chip}
        aria-busy={checking || undefined}
        style={{
          alignItems: "center",
          background: "var(--lc-input-background)",
          border:
            "var(--lc-border-width) var(--lc-border-style) var(--lc-input-border)",
          borderRadius: 999,
          boxSizing: "border-box",
          display: "inline-flex",
          fontSize: size.small,
          gap: 6,
          gridColumn: settled ? "1 / -1" : undefined,
          justifySelf: settled ? "center" : "end",
          maxWidth: "100%",
          minWidth: 0,
          // Without the ×, the right side matches the tick's.
          padding: settled || !onChange ? "3px 8px 3px 6px" : "3px 4px 3px 6px",
          transition: `padding ${CHIP_MS}ms ${MOTION.ease}`,
        }}
      >
        {guest ? null : (
          <span
            aria-label={verified ? "Verified" : undefined}
            role={verified ? "img" : undefined}
            style={{
              alignItems: "center",
              background: verified ? "#34c759" : "transparent",
              border: verified ? 0 : "1.5px solid var(--lc-muted)",
              borderRadius: 999,
              boxSizing: "border-box",
              color: "#fff",
              display: "inline-flex",
              flex: "none",
              height: 16,
              justifyContent: "center",
              opacity: verified ? 1 : 0.5,
              transition: `background ${MOTION.durationMs}ms ${MOTION.ease}`,
              width: 16,
            }}
          >
            {verified ? (
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                aria-hidden="true"
              >
                <path
                  d="M2 5.2 4.1 7.2 8 3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : null}
          </span>
        )}
        <span
          style={{
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {email}
        </span>
        {onChange ? (
          <button
            type="button"
            aria-label="Change email"
            disabled={disabled || settled}
            onClick={onChange}
            style={{
              ...button.link,
              alignItems: "center",
              display: "inline-flex",
              flex: "none",
              fontSize: "1em",
              height: 20,
              justifyContent: "center",
              lineHeight: 1,
              // Paying: the × fades as its space closes (the gap before it
              // too), so the chip draws in around the address.
              marginLeft: settled ? -6 : 0,
              opacity: settled ? 0 : 1,
              overflow: "hidden",
              textDecoration: "none",
              transition: `opacity ${settled ? 150 : 300}ms ease, width ${CHIP_MS}ms ${MOTION.ease}, margin-left ${CHIP_MS}ms ${MOTION.ease}`,
              width: settled ? 0 : 20,
            }}
          >
            ×
          </button>
        ) : null}
      </span>
    </div>
  );
}
